import { Component, EventEmitter, Input, Output, OnInit, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { AccessMenuRole } from '../../../../../core/models/system-settings/manage-roles-menu.model';
import { MenuListOption } from '../../../../../core/models/system-settings/menu-lists.model';
import { ApiSystemSettingsService } from '../../../../../core/services/api/system-settings/api-system-settings.service';
import { MapperManageRolesMenuService } from '../../../../../core/mappers/system-settings/mapper-manage-roles-menu.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-manage-role-menu',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-manage-role-menu.component.html',
  styleUrl: './modal-manage-role-menu.component.css'
})
export class ModalManageRoleMenuComponent implements OnInit {
  @Input() data: AccessMenuRole | null = null;
  @Input() roleId!: number;
  @Input() roleName = '';
  @Input() groupSubId = 0;
  @Input() groupSubName = '';
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  formData: Partial<AccessMenuRole> = {
    id: 0,
    name: '',
    menuListId: 0,
    menuListName: '',
    roleId: 0,
    roleName: '',
    canView: true,
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canAction: false,
  };

  menuListOptions = signal<MenuListOption[]>([]);
  isLoading = signal<boolean>(false);
  isOptionsLoading = signal<boolean>(false);

  searchMenuText = '';
  openDropdown: string | null = null;

  private toast = inject(ToastService);
  private apiService = inject(ApiSystemSettingsService);
  private mapper = inject(MapperManageRolesMenuService);
  private cookieService = inject(CookieService);
  private router = inject(Router);
  private permissionService = inject(MenuPermissionService);

  // ================= Normal Functions =================

  ngOnInit() {
    this.formData.roleId = this.roleId;
    this.formData.roleName = this.roleName;

    if (this.data) {
      this.formData = { ...this.data };
    }

    // Always force canView to true
    this.formData.canView = true;

    this.loadMenuListOptions();
  }

  closeDropdowns(): void {
    if (this.openDropdown === 'menuList') {
      const options = this.menuListOptions() || [];
      const match = options.find(
        (o) => o?.name?.toLowerCase() === this.searchMenuText?.trim()?.toLowerCase()
      );

      if (match) {
        if (this.formData.menuListId !== match.id) {
          this.formData.menuListId = match.id;
          this.onMenuListChange(match.id);
        }
        this.searchMenuText = match.name;
      } else {
        const matchedOption = options.find(o => o.id === this.formData.menuListId);
        this.searchMenuText = matchedOption ? matchedOption.name : '';
      }
    }
    this.openDropdown = null;
  }

  get filteredMenuListOptions(): MenuListOption[] {
    const query = (this.searchMenuText || '').toLowerCase().trim();
    const options = this.menuListOptions() || [];
    if (!query) return options;
    return options.filter(o => o?.name?.toLowerCase().includes(query));
  }

  onInputFocus(menu: string): void {
    if (this.openDropdown && this.openDropdown !== menu) {
      this.closeDropdowns();
    }
    this.openDropdown = menu;
  }

  onClickInsideModal(event: Event): void {
    event.stopPropagation();
    this.closeDropdowns();
  }

  toggleDropdown(dropdown: string, event: Event): void {
    event.stopPropagation();
    if (this.openDropdown === dropdown) {
      this.closeDropdowns();
    } else {
      if (this.openDropdown) this.closeDropdowns();
      this.openDropdown = dropdown;
    }
  }

  selectMenu(opt: MenuListOption): void {
    this.formData.menuListId = opt.id;
    this.onMenuListChange(opt.id);
    this.searchMenuText = opt.name;
    this.openDropdown = null;
  }

  onClose() {
    this.close.emit();
  }

  onMenuListChange(menuListId: number) {
    const option = this.menuListOptions().find(o => o.id === menuListId);
    if (option && !this.formData.id) {
      this.formData.name = option.name;
      this.formData.menuListName = option.name;
    }
  }

  onSave() {
    const path = this.router.url;
    const isEdit = !!this.formData.id;

    if (isEdit && !this.permissionService.canUpdate(path)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!isEdit && !this.permissionService.canCreate(path)) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }

    if (!this.formData.menuListId || this.formData.menuListId <= 0) {
      this.toast.error('กรุณาเลือกเมนู');
      return;
    }

    this.isLoading.set(true);

    const userIdStr = this.cookieService.get('user_id');
    const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
    const menuId = this.permissionService.getMenuId(path) || 0;

    const payload = this.mapper.mapAccessMenuRoleToPayload(
      this.formData as AccessMenuRole,
      userId,
      menuId
    );

    if (isEdit) {
      this.apiService.updateMenuRole(payload, this.formData.id!).subscribe({
        next: (res: any) => {
          console.log("update data : ", res, JSON.stringify(res));
          if (res?.isSuccess) {
            this.toast.success('แก้ไขข้อมูลสำเร็จ');
            this.saveSuccess.emit();
            this.close.emit();
          } else {
            this.toast.error(res?.message || 'แก้ไขข้อมูลไม่สำเร็จ');
          }
          this.isLoading.set(false);
        },
        error: (err: any) => {
          console.error(err);
          this.toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
          this.isLoading.set(false);
        }
      });
    } else {
      this.apiService.createMenuRole(payload).subscribe({
        next: (res: any) => {
          console.log("create data : ", res, JSON.stringify(res));
          if (res?.isSuccess) {
            this.toast.success('สร้างข้อมูลสำเร็จ');
            this.saveSuccess.emit();
            this.close.emit();
          } else {
            this.toast.error(res?.message || 'สร้างข้อมูลไม่สำเร็จ');
          }
          this.isLoading.set(false);
        },
        error: (err: any) => {
          console.error(err);
          this.toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
          this.isLoading.set(false);
        }
      });
    }
  }

  private loadMenuListOptions(): void {
    this.isOptionsLoading.set(true);
    const subId = this.groupSubId > 0 ? this.groupSubId : undefined;
    const includeMenuListId = this.formData.id && this.formData.menuListId
      ? this.formData.menuListId
      : undefined;

    this.apiService.getMenuListOptions(undefined, subId, this.roleId, includeMenuListId).subscribe({
      next: (options) => {
        console.log("Option Menu List : ", options);
        const list = options || [];
        this.menuListOptions.set(list);

        if (this.formData.menuListId) {
          const matchedOption = list.find(o => o.id === this.formData.menuListId);
          this.searchMenuText = matchedOption ? matchedOption.name : '';
        }

        if (!this.formData.id && list.length === 0) {
          this.toast.info('ไม่มีเมนูที่เพิ่มได้ — เมนูในกลุ่มนี้ถูกผูกกับ Role แล้วทั้งหมด');
        }
        this.isOptionsLoading.set(false);
      },
      error: () => {
        this.menuListOptions.set([]);
        this.isOptionsLoading.set(false);
      }
    });
  }

  get canShowActivityHistory(): boolean {
    return !!this.formData.id && this.formData.id > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
